import React, { useEffect } from "react";
import { DealStage } from "../lib/dealStages";
import { capture } from "../lib/analytics";
import { Property } from "../types";
import PropertyDetail from "./PropertyDetail";
import PhotoGallery from "./PhotoGallery";
import LifecycleBar from "./LifecycleBar";
import WatchlistToggle from "./WatchlistToggle";
import DealScore from "./DealScore";
import CodeViolationEvidence from "./CodeViolationEvidence";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { statusMeta } from "../lib/statusMeta";
import { resolveDisplayAddress } from "../lib/dataQuality";
import { formatCurrencyShort as sharedFormatCurrencyShort } from "../lib/format";

interface PropertyModalProps {
  property: Property;
  onClose: () => void;
  authed: boolean;
  isPro: boolean;
  // isAllowedCounty — geography-aware tier gate (Free/Scout redesign). True
  // for Pro/team/admin, or when this property's county is one of the
  // viewer's chosen counties. Gates the same assessed-value/equity display
  // PropertyCard fuzzes; bid history stays isPro-only (separate mechanism).
  isAllowedCounty: boolean;
  isWatched: boolean;
  watchLoading?: boolean;
  watchLimit: number;
  watchLimitMax: number;
  onToggleWatch: (propertyId: string) => void;
  // "Not interested" (#3074): omit to render no hide control.
  isHidden?: boolean;
  onToggleHidden?: (propertyId: string) => void;
  onRequireAuth: () => void;
  onRequirePro: () => void;
  // Set only for a free/scout viewer with no chosen county: the fuzzed
  // "outside your area" CTA opens the county picker instead of the upgrade.
  onPickCounty?: () => void;
  getNotes: (propertyId: string) => string;
  updateNotes: (propertyId: string, notes: string) => Promise<void>;
  getStage?: (propertyId: string) => DealStage | null;
  setStage?: (propertyId: string, stage: DealStage | null) => void;
  // activeLeadTypeFilter — the lead-type filter active on the list this
  // property was opened from. Threaded straight through to PropertyDetail;
  // see its own prop doc for the full rationale.
  activeLeadTypeFilter?: string | string[];
}

// Delegates to lib/format.ts (#1033). Falls back to "" (not the shared
// default "$0") — both call sites already guard with `value > 0 &&` /
// `equity > 0 &&` before calling this, so the fallback branch is dead code
// either way; kept explicit for clarity rather than relying on that.
function formatCurrencyShort(value: number | null | undefined): string {
  return sharedFormatCurrencyShort(value, "");
}

const PropertyModal: React.FC<PropertyModalProps> = ({
  property,
  onClose,
  authed,
  isPro,
  isAllowedCounty,
  isWatched,
  watchLoading = false,
  watchLimit,
  watchLimitMax,
  onToggleWatch,
  isHidden = false,
  onToggleHidden,
  onRequireAuth,
  onRequirePro,
  onPickCounty,
  getNotes,
  updateNotes,
  getStage,
  setStage,
  activeLeadTypeFilter,
}) => {
  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape key closes modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // atLimit — watchLimitMax already encodes the correct per-plan cap (see
  // lib/planCapabilities.ts); no separate isPro shortcut needed here. See
  // PropertyCard.tsx's atLimit comment for the #677 bug this used to have
  // (Scout's own cap of 10 never visually triggered because `!isPro` is
  // always false for Scout).
  const atLimit = !isWatched && watchLimit >= watchLimitMax;

  // #2301: reconciled address/city/zip, NOT raw property.address/city/zipCode
  // read directly — those three can disagree with each other (a raw address
  // string that already has city/zip baked in vs. separately-sourced typed
  // columns), which is exactly what produced a live report of two different
  // zip codes for one property on this same header. resolveDisplayAddress is
  // the single reconciled source PropertyCard.tsx and the rest of
  // PropertyDetail.tsx already use.
  const displayAddr = resolveDisplayAddress(property);

  // Building details, server-typed (#1872) — no `as number` casts over a
  // schemaless blob.
  const beds = property.building?.bedrooms ?? null;
  const baths = property.building?.fullBaths ?? null;
  const halfBaths = property.building?.halfBaths ?? null;

  const equity =
    property.assessedValue && property.amountDue
      ? property.assessedValue - property.amountDue
      : null;

  const handleShareLink = () => {
    // ref=share: a shared link is a marketing entry point, so a logged-out
    // recipient lands on "Create account". Untagged deep links (alert and
    // digest emails, which go to existing accounts) open on "Sign in".
    const url = `${window.location.origin}/app?property=${property.id}&ref=share`;
    navigator.clipboard.writeText(url);
  };

  const modalRef = useFocusTrap<HTMLDivElement>();

  return (
    <div
      className="property-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="property-modal"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-modal-title"
      >
        {/* Wrapped in a sticky container so the close button stays visible
            regardless of scroll position inside the modal (#186). The
            wrapper has zero height + negative margin so the hero photo
            still starts at the top of the modal — the button overlays it. */}
        <div className="property-modal-close-wrap">
          <button
            className="property-modal-close"
            onClick={onClose}
            aria-label="Close property details"
          >
            &times;
          </button>
        </div>

        {/* Lifecycle bar at the very top (#audit-5).
            Previously rendered below the hero + header — buried at
            ~420px y-offset, often below the fold on a 13" laptop. Now
            it's the user's orientation anchor: which stage of NC
            foreclosure is this property in? Detail variant adds a
            below-the-bar caption explaining the current stage. */}
        <div className="modal-lifecycle modal-lifecycle--top">
          <LifecycleBar status={property.status} variant="detail" />
        </div>

        {/* Deal Score (PR-D #audit-6, closes #158).
            Click to expand → factor breakdown (Equity / Actionability /
            Urgency / Data sanity). Sits between the lifecycle bar and
            the hero photo so the score is the second thing the user sees,
            paired with the lifecycle stage above.
            Hidden for terminal-stage properties (#246) — a score on a
            sold/dismissed/withdrawn property can't be acted on and is
            confusing. The lifecycle bar above already shows that the
            property is resolved. */}
        {!statusMeta(property.status).isTerminal && (
          <div className="modal-deal-score">
            <DealScore property={property} variant="full" />
          </div>
        )}

        {/* Hero — Photo or satellite fallback */}
        <div className="modal-hero">
          <PhotoGallery property={property} />
        </div>

        {/* Header — Address + key stats + actions */}
        <div className="modal-header">
          <div className="modal-title-block">
            <h1 className="modal-address" id="property-modal-title">{displayAddr.text}</h1>
            <p className="modal-meta">
              {[displayAddr.city, property.state, displayAddr.zip].filter(Boolean).join(", ")}
              {property.propertyType ? ` \u00B7 ${property.propertyType}` : ""}
              {beds || baths ? " \u00B7 " : ""}
              {beds ? `${beds}bd` : ""}
              {baths ? ` ${baths}ba` : ""}
              {halfBaths && halfBaths > 0 ? ` +${halfBaths}hba` : ""}
            </p>
            <div className="modal-price-row">
              {isAllowedCounty ? (
                <>
                  {property.assessedValue > 0 && (
                    <span className="modal-price">{formatCurrencyShort(property.assessedValue)}</span>
                  )}
                  {equity !== null && equity > 0 && (
                    <span className="modal-equity">+{formatCurrencyShort(equity)} equity</span>
                  )}
                </>
              ) : (
                <span className="modal-price-fuzzed">
                  <span className="modal-price-fuzzed-bar" aria-hidden="true" />
                  Outside your area —{" "}
                  <button type="button" className="modal-price-fuzzed-cta" onClick={() => (onPickCounty ? onPickCounty() : onRequirePro())}>
                    {onPickCounty ? "pick your county to see value" : "upgrade to see value"}
                  </button>
                </span>
              )}
            </div>
          </div>
          <div className="modal-actions">
            <WatchlistToggle
              isWatched={isWatched}
              loading={watchLoading}
              authed={authed}
              proRequired={atLimit}
              onToggle={() => onToggleWatch(property.id)}
              onRequireAuth={onRequireAuth}
              onRequirePro={onRequirePro}
            />
            <button className="modal-share-btn" onClick={handleShareLink} title="Copy shareable link">
              Share
            </button>
            {/* One-page printout for a lender, partner or contractor (#3076).
                App.css's @media print block hides everything but this modal. */}
            <button
              type="button"
              className="modal-share-btn"
              onClick={() => {
                capture("property_printed", { property_id: property.id, county_id: property.countyId });
                window.print();
              }}
              title="Print a one-page summary"
            >
              Print
            </button>
            {authed && onToggleHidden && (
              <button
                type="button"
                className="modal-share-btn"
                onClick={() => onToggleHidden(property.id)}
                title={isHidden ? "Show this property in your map and lists again" : "Hide this property from your map and lists"}
              >
                {isHidden ? "Unhide" : "Not interested"}
              </button>
            )}
          </div>
        </div>

        {/* (Lifecycle bar moved to top of modal in PR-C redesign so it
            serves as the user's orientation anchor.) */}

        {/* Property detail content — existing component with all data sections */}
        <div className="modal-content">
          {/* Code-violation evidence: open cases, order date, stage and a
              source link, so the lead can be verified. Renders nothing for a
              property without a code_enforcement/condemnation signal. Shown to
              in-area viewers only, like the lead pill it explains. */}
          {isAllowedCounty && <CodeViolationEvidence property={property} />}
          <PropertyDetail
            property={property}
            authed={authed}
            isWatched={isWatched}
            isPro={isPro}
            isAllowedCounty={isAllowedCounty}
            onRequirePro={onRequirePro}
            onPickCounty={onPickCounty}
            getNotes={getNotes}
            updateNotes={updateNotes}
            getStage={getStage}
            setStage={setStage}
            activeLeadTypeFilter={activeLeadTypeFilter}
          />
        </div>
        <div className="print-footer" aria-hidden="true">
          Printed {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} from
          Parcely · parcelyapp.com · Public records; verify with the county before you bid.
        </div>
      </div>
    </div>
  );
};

export default PropertyModal;
